import { useEffect, useState } from 'react'
import { Database, Lock } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { ErrorState, PermissionDenied, Skeleton } from '@/components/States'
import { ApiError } from '@/lib/api'
import { fetchTables, fetchRows, PLAT_STR as S, type DbTable, type DbRows } from './platformApi'

const PAGE = 50

/**
 * A0 — Модели (БД): READ-ONLY jadval ko'ruvchi. `GET /admin/db/tables` chap ro'yxat,
 * tanlangan jadval `GET /admin/db/tables/:table` (maxfiy ustunlar '***', server
 * SUPERADMIN majburlaydi → 403 → <PermissionDenied>). Yozish YO'Q.
 */
export function DbBrowserScreen() {
  const [tables, setTables] = useState<DbTable[] | null>(null)
  const [err, setErr] = useState<ApiError | null>(null)
  const [sel, setSel] = useState<string | null>(null)
  const [offset, setOffset] = useState(0)
  const [data, setData] = useState<DbRows | null>(null)
  const [loadingRows, setLoadingRows] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    fetchTables(ac.signal)
      .then(setTables)
      .catch((e) => {
        if (!ac.signal.aborted) setErr(e instanceof ApiError ? e : new ApiError('INTERNAL', String(e), 500))
      })
    return () => ac.abort()
  }, [])

  useEffect(() => {
    if (!sel) return
    const ac = new AbortController()
    setLoadingRows(true)
    fetchRows(sel, PAGE, offset, ac.signal)
      .then((d) => setData(d))
      .catch((e) => {
        if (!ac.signal.aborted) setErr(e instanceof ApiError ? e : new ApiError('INTERNAL', String(e), 500))
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoadingRows(false)
      })
    return () => ac.abort()
  }, [sel, offset])

  const open = (t: string) => {
    setSel(t)
    setOffset(0)
    setData(null)
  }

  if (err?.isForbidden) return <PermissionDenied permission="db:read" />

  return (
    <>
      <PageHeader title={S.db} screenId="A0" description={S.dbDesc} />
      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/* Chap: jadval ro'yxati */}
        <aside className="w-56 shrink-0 overflow-auto border-r border-border-default bg-surface-2">
          {err && !err.isForbidden ? (
            <div className="p-3">
              <ErrorState code={err.code} />
            </div>
          ) : !tables ? (
            <div className="space-y-1.5 p-3">
              {Array.from({ length: 12 }).map((_, i) => (
                <Skeleton key={i} className="h-6 w-full" />
              ))}
            </div>
          ) : (
            <ul className="py-1">
              {tables.map((t) => (
                <li key={t.table}>
                  <button
                    type="button"
                    onClick={() => open(t.table)}
                    className={`flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm hover:bg-surface-3 ${
                      sel === t.table ? 'bg-surface-3 text-primary' : 'text-secondary'
                    }`}
                  >
                    <span className="flex min-w-0 items-center gap-1.5">
                      <Database size={13} className="shrink-0 text-tertiary" />
                      <span className="truncate font-mono">{t.table}</span>
                    </span>
                    <span className="shrink-0 text-xs text-tertiary tabular">
                      {t.rows_estimate.toLocaleString()}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </aside>

        {/* O'ng: qatorlar */}
        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          <div className="flex items-center gap-2 border-b border-border-subtle bg-surface-1 px-4 py-2 text-xs text-tertiary">
            <Lock size={12} />
            {S.readonly}
          </div>
          <div className="min-h-0 flex-1 overflow-auto">
            {!sel ? (
              <div className="flex h-full items-center justify-center text-sm text-tertiary">
                {S.selectTable}
              </div>
            ) : loadingRows && !data ? (
              <div className="space-y-1.5 p-4">
                {Array.from({ length: 12 }).map((_, i) => (
                  <Skeleton key={i} className="h-6 w-full" />
                ))}
              </div>
            ) : data && data.rows.length === 0 ? (
              <div className="flex h-full items-center justify-center text-sm text-tertiary">
                {S.empty}
              </div>
            ) : data ? (
              <table className="w-full border-collapse text-left text-sm">
                <thead className="sticky top-0 bg-surface-2">
                  <tr>
                    {data.columns.map((c) => (
                      <th
                        key={c}
                        className="whitespace-nowrap border-b border-border-default px-3 py-2 font-mono text-xs font-medium text-secondary"
                      >
                        {c}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r, i) => (
                    <tr key={i} className="hover:bg-surface-2">
                      {data.columns.map((c) => (
                        <td
                          key={c}
                          className="max-w-xs truncate whitespace-nowrap border-b border-border-subtle px-3 py-1.5 text-secondary"
                          title={r[c] ?? ''}
                        >
                          {r[c] ?? <span className="text-tertiary">NULL</span>}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : null}
          </div>
          {sel && (
            <div className="flex items-center justify-between border-t border-border-default bg-surface-1 px-4 py-2 text-sm">
              <span className="text-tertiary tabular">
                {offset + 1}–{offset + (data?.rows.length ?? 0)}
              </span>
              <span className="flex gap-2">
                <button
                  type="button"
                  disabled={offset === 0 || loadingRows}
                  onClick={() => setOffset(Math.max(0, offset - PAGE))}
                  className="rounded-md border border-border-default px-3 py-1 text-secondary hover:text-primary disabled:opacity-40"
                >
                  {S.prev}
                </button>
                <button
                  type="button"
                  disabled={loadingRows || (data?.rows.length ?? 0) < PAGE}
                  onClick={() => setOffset(offset + PAGE)}
                  className="rounded-md border border-border-default px-3 py-1 text-secondary hover:text-primary disabled:opacity-40"
                >
                  {S.next}
                </button>
              </span>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
